/* eslint-disable @typescript-eslint/no-unused-vars */
import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { Observable } from 'rxjs';
import { BtnType, getLaunchFlags, Icon, setSignup } from '@core';
import { Code } from '@model';
import { ngIfAnim } from '@animations';
import { SpProgram } from '@session';
import { Registration, RegistrationResponse } from '@model/entities/registration';
import { WhereDidYouHearCodes, WhereDidYouHearOptions } from 'import-server/src/model/codes-enums';
import { RegistrationService } from '@entity/entity-services/registration.service';
import { SpProgramService } from '@session/sp-program.service';
import { AuthService } from '@core/services/auth-services/auth.service';
import { NavMenuService, NavMenuVm } from '@app/app-components/nav/nav-menu.service';
import { NgForm, NgModel } from '@angular/forms';
import { CaptchaComponent } from '../app-components/captcha.component';
import { environment } from '@environment';
import { CookieService } from 'ngx-cookie-service';

const isMocking = environment.mockServices;
const pendoScript = `
(function (p, e, n, d, o) {
        var v, w, x, y, z; o = p[d] = p[d] || {}; o._q = [];
        v = ['initialize', 'identify', 'updateOptions', 'pageLoad']; for (w = 0, x = v.length; w < x; ++w)(function (m) {
            o[m] = o[m] || function () { o._q[m === v[0] ? 'unshift' : 'push']([m].concat([].slice.call(arguments, 0))); };
        })(v[w]);
        y = e.createElement(n); y.async = !0; y.src = 'https://cdn.pendo.io/agent/static/bf506cfa-b006-4806-5f7d-810298f1ff59/pendo.js';
        z = e.getElementsByTagName(n)[0]; z.parentNode.insertBefore(y, z);
    })(window, document, 'script', 'pendo');


    pendo.initialize({
        apiKey: 'bf506cfa-b006-4806-5f7d-810298f1ff59',
        visitor: {},
        account: {}
        });`;

@Component({
  selector: 'sp-get-start',
  templateUrl: './get-start.component.html',
  animations: [ngIfAnim],
  providers: [CookieService]
})
export class GetStartComponent implements OnInit, AfterViewInit {
  @ViewChild('form') form: NgForm;
  @ViewChild('emailInput') emailModel: NgModel;
  displaywhereDidYouHearDetail: boolean;
  whereDidYouHearDetailPlaceholder: string;
  btn = BtnType;
  icon = Icon;
  whereDidYouHearOptions = WhereDidYouHearOptions.codes;
  vm: Registration;
  navmenuvm$: Observable<NavMenuVm>;
  program: SpProgram;
  spForceFeedback = false;

  @ViewChild('captcha') captcha: CaptchaComponent;
  private captchaResponse: string;

  captchaEnabled: boolean = environment.enableRegistrationCaptcha === 'true';

  constructor(
    private registrationService: RegistrationService,
    private auth0Service: AuthService,
    private spProgramService: SpProgramService,
    private cookieService: CookieService,
    navMenuService: NavMenuService
  ) {
    this.navmenuvm$ = navMenuService.vm$;
    if (isMocking || environment.excludeScripts) {
      return; // don't load pendo when mocking or excludeScripts is ON
    }
    injectScript(pendoScript);
  }

  ngOnInit(): void {
    this.vm = { ...this.vm };
    this.setWhereDidYouHearOptions();
    this.spProgramService
      .loadProgramFromCurrentLocationAndPartnerCode()
      .subscribe((s) => (this.program = s));
    window.scrollTo(0, 0);
    this.getCookieData();
  }

  ngAfterViewInit(): void {
    if (this.captchaEnabled) {
      this.captcha.Response.subscribe({
        next: (v) => (this.captchaResponse = v)
      });
    }
  }

  get disabled() {
    return false;
  }
  get shouldNotDisplay() {
    return this.program?.programCode?.trim() === 'TAP';
  }
  createAccount() {
    this.spForceFeedback = true;
    if (!this.form.valid) {
      return false; // don't save if form is invalid, no user, or no captcha
    }
    if (this.captchaEnabled && !this.captchaResponse) {
      this.vm.isSuccess = false;
      this.vm.validationErrors = [
        { description: 'Please select reCAPTCHA', entityId: null, entityType: null }
      ];
      window.scroll({
        top: 0,
        left: 0,
        behavior: 'smooth'
      });
      return false;
    }
    const request: Registration = {
      ...this.vm,
      initialUrl: getLaunchFlags(),
      domainName: window.location.host,
      captchaResponse: this.captchaResponse
    };
    this.registrationService.createAccount(request).subscribe((result: RegistrationResponse) => {
      if (result && result.isSuccess === true) {
        result.email = this.vm.email;
        setSignup(result);
        void this.auth0Service.start();
      } else if (result && !result.isSuccess) {
        this.vm.isSuccess = false;
        this.vm.validationErrors = result.validationErrors;
        window.scroll({
          top: 0,
          left: 0,
          behavior: 'smooth'
        });
        this.vm.validationErrors.forEach((errorMsg) => {
          if (errorMsg.description === 'Email address is not allowed for registration purposes') {
            this.emailModel.control.setErrors({ invalidValues: true });
          }
        });
      }
    });
  }

  getEmployeeLoginLink(vm: NavMenuVm) {
    if (vm?.skin?.productLandingPageEmployee) {
      return vm.skin.productLandingPageEmployee;
    }
    if (this.program?.productLandingPageEmployee) {
      return this.program.productLandingPageEmployee;
    }
  }

  get loginUrl() {
    return `https://${window.location.host}/?&useauth0=on&sendto=signin`;
  }

  get termsPage() {
    return `https://${window.location.host}/register/terms`;
  }

  onWhereDidYouHearAnswerChanged(el: EventTarget) {
    const whereDidYouHearAnswerValue = (el as HTMLSelectElement).value as WhereDidYouHearCodes;
    this.displaywhereDidYouHearDetail = false;
    switch (whereDidYouHearAnswerValue) {
      case WhereDidYouHearCodes.OnlineReviews:
        this.displaywhereDidYouHearDetail = true;
        this.whereDidYouHearDetailPlaceholder = 'Review site';
        break;
      case WhereDidYouHearCodes.MyAccountant:
        this.displaywhereDidYouHearDetail = true;
        this.whereDidYouHearDetailPlaceholder = 'Accountant name';
        break;
      case WhereDidYouHearCodes.Other:
        this.displaywhereDidYouHearDetail = true;
        this.whereDidYouHearDetailPlaceholder = 'Tell us more';
        break;
    }
  }

  setWhereDidYouHearOptions() {
    this.whereDidYouHearOptions = this.shuffle(
      WhereDidYouHearOptions.codes.filter((item) => item.value !== WhereDidYouHearCodes.Other)
    );
    this.whereDidYouHearOptions.push(
      WhereDidYouHearOptions.codes.find((item) => item.value === WhereDidYouHearCodes.Other)
    );
  }


  shuffle(arr: Code[]) {
    return arr
      .map((a) => [Math.random(), a])
      .sort((a, b) => (a[0] as number) - (b[0] as number))
      .map((a) => a[1]) as Code[];
  }

  /* Get cookie data from market site(first name, phone, email) */
  getCookieData() {
    const cookieName = this.cookieService.get('opt_id1');
    const cookieEmail = this.cookieService.get('opt_id2');
    const cookiePhoneNumber = this.cookieService.get('opt_id3');

    if (cookieName) {
      const names = splitName(cookieName);

      this.vm.firstName = names.firstName;
      this.vm.lastName = names.lastName;
    }

    if (cookieEmail) {
      this.vm.email = cookieEmail;
    }

    if (cookiePhoneNumber) {
      this.vm.phone = cookiePhoneNumber;
    }
  }
}

/** Inject Script in DOM */
function injectScript(scriptCode: string) {
  const ref = window.document.getElementsByTagName('script')[0];
  const script = window.document.createElement('script');
  script.text = scriptCode;
  ref.parentNode.insertBefore(script, ref);
}

/* Check if name from cookie is just a first name, or has last name in it */
function splitName(fullName: string) {
  const nameSplit = fullName.trim().split(' ');
  const firstName = nameSplit[0] || '';
  const lastName = nameSplit.length > 1 ? nameSplit[nameSplit.length - 1] : '';
  return { firstName, lastName };
}
